import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './ManageAnnouncements.css';

const ManageAnnouncements = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [announcements, setAnnouncements] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    course_id: courseId || '',
    title: '',
    content: '',
    announcement_type: 'info',
    is_pinned: false,
    send_email: false,
    send_sms: false,
    send_whatsapp: false,
    expires_at: '',
    link_url: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [courseId]);

  const fetchData = async () => {
    try {
      const requests = [];

      if (isAdmin) {
        requests.push(api.get('/announcements/admin/all'));
        requests.push(api.get('/courses'));
      } else {
        requests.push(api.get('/announcements/instructor'));
        requests.push(api.get(`/courses?instructor_id=${user.id}`));
      }

      const [announcementsRes, coursesRes] = await Promise.all(requests);
      setAnnouncements(isAdmin ? announcementsRes.data.announcements : announcementsRes.data);
      setCourses(coursesRes.data.courses || coursesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingId) {
        await api.put(`/announcements/${editingId}`, formData);
      } else {
        const endpoint = formData.course_id
          ? `/announcements/course/${formData.course_id}`
          : '/announcements/global';
        await api.post(endpoint, formData);
      }

      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert('Failed to save announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (announcement) => {
    setFormData({
      course_id: announcement.course_id || '',
      title: announcement.title,
      content: announcement.content,
      announcement_type: announcement.announcement_type,
      is_pinned: announcement.is_pinned,
      send_email: false,
      send_sms: false,
      send_whatsapp: false,
      expires_at: announcement.expires_at
        ? new Date(announcement.expires_at).toISOString().slice(0, 16)
        : '',
      link_url: announcement.link_url || '',
    });
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      await api.delete(`/announcements/${id}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert('Failed to delete announcement');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await api.put(`/announcements/${id}/status`, {
        is_active: !currentStatus,
      });
      fetchData();
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update announcement status');
    }
  };

  const resetForm = () => {
    setFormData({
      course_id: courseId || '',
      title: '',
      content: '',
      announcement_type: 'info',
      is_pinned: false,
      send_email: false,
      send_sms: false,
      send_whatsapp: false,
      expires_at: '',
      link_url: '',
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'info': return '#3b82f6';
      case 'warning': return '#f59e0b';
      case 'success': return '#10b981';
      case 'urgent': return '#ef4444';
      default: return '#64748b';
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page manage-announcements-page">
      <div className="container">
        <header className="page-header">
          <div>
            <h1>Manage Announcements</h1>
            <p>Create and manage announcements for your students</p>
          </div>
          <button
            className="btn-primary"
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              resetForm();
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Announcement
          </button>
        </header>

        {/* Form Modal */}
        {showForm && (
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingId ? 'Edit Announcement' : 'Create Announcement'}</h2>
                <button className="close-btn" onClick={() => setShowForm(false)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Course</label>
                    <select
                      name="course_id"
                      value={formData.course_id}
                      onChange={handleInputChange}
                      disabled={!!courseId}
                    >
                      {isAdmin && <option value="">Global (All Users)</option>}
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Type</label>
                    <select
                      name="announcement_type"
                      value={formData.announcement_type}
                      onChange={handleInputChange}
                    >
                      <option value="info">Info</option>
                      <option value="success">Success</option>
                      <option value="warning">Warning</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Announcement title"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Content</label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="Write your announcement here..."
                    rows="5"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Meeting / External Link (Optional)</label>
                  <input
                    type="url"
                    name="link_url"
                    value={formData.link_url}
                    onChange={handleInputChange}
                    placeholder="https://meet.google.com/... or Zoom link"
                  />
                  <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>
                    Students will see a button to join the meeting directly from the announcement.
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Expires At (Optional)</label>
                    <input
                      type="datetime-local"
                      name="expires_at"
                      value={formData.expires_at}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_pinned"
                      checked={formData.is_pinned}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark" />
                    Pin this announcement
                  </label>
                </div>

                <div className="notification-options">
                  <h4>Send Notifications via GoHighLevel</h4>
                  <p className="helper-text">
                    Select channels to notify students (requires GoHighLevel integration)
                  </p>
                  <div className="checkbox-row">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="send_email"
                        checked={formData.send_email}
                        onChange={handleInputChange}
                      />
                      <span className="checkmark" />
                      Email
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="send_sms"
                        checked={formData.send_sms}
                        onChange={handleInputChange}
                      />
                      <span className="checkmark" />
                      SMS
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="send_whatsapp"
                        checked={formData.send_whatsapp}
                        onChange={handleInputChange}
                      />
                      <span className="checkmark" />
                      WhatsApp
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowForm(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? 'Saving...' : editingId ? 'Update' : 'Publish'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Announcements List */}
        <div className="announcements-table">
          <div className={`table-header ${isAdmin ? 'with-status' : ''}`}>
            <span>Announcement</span>
            <span>Course</span>
            <span>Type</span>
            {isAdmin && <span>Status</span>}
            <span>Published</span>
            <span>Actions</span>
          </div>

          {announcements.length === 0 ? (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <p>No announcements yet</p>
              <button className="btn-primary" onClick={() => setShowForm(true)}>
                Create your first announcement
              </button>
            </div>
          ) : (
            announcements.map((announcement) => (
              <div key={announcement.id} className={`table-row ${isAdmin ? 'with-status' : ''} ${announcement.is_active === false ? 'inactive' : ''}`}>
                <div className="announcement-cell">
                  <div className="announcement-info">
                    {announcement.is_pinned && (
                      <span className="pin-icon" title="Pinned">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                        </svg>
                      </span>
                    )}
                    <div>
                      <h4>{announcement.title}</h4>
                      <p>{announcement.content.substring(0, 80)}...</p>
                    </div>
                  </div>
                </div>
                <div className="course-cell">
                  {announcement.course_title || (
                    <span className="global-badge">Global</span>
                  )}
                </div>
                <div className="type-cell">
                  <span
                    className="type-badge"
                    style={{ backgroundColor: getTypeColor(announcement.announcement_type) + '20', color: getTypeColor(announcement.announcement_type) }}
                  >
                    {announcement.announcement_type}
                  </span>
                </div>
                {isAdmin && (
                  <div className="status-cell">
                    <button
                      className={`status-toggle ${announcement.is_active !== false ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus(announcement.id, announcement.is_active !== false)}
                      title={announcement.is_active !== false ? 'Click to deactivate' : 'Click to activate'}
                    >
                      <span className="toggle-track">
                        <span className="toggle-thumb" />
                      </span>
                      <span className="toggle-label">
                        {announcement.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </button>
                  </div>
                )}
                <div className="date-cell">
                  {new Date(announcement.published_at).toLocaleDateString()}
                </div>
                <div className="actions-cell">
                  <button
                    className="icon-btn"
                    onClick={() => handleEdit(announcement)}
                    title="Edit"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button
                    className="icon-btn danger"
                    onClick={() => handleDelete(announcement.id)}
                    title="Delete"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageAnnouncements;
